import React, { useCallback, useContext, useMemo, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { ViewModifierContext, IViewModifierContext } from '../../viewModifierState/viewModifierContext';
import { Link } from 'react-router-dom';
import { RequestEntity, EntityStatus } from 'types';
import { useTranslation } from 'react-i18next';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { portionSelectionModes } from 'appConstants/dishes';
import ImgUploaderByEntityId from 'components/ImgUploader/ImgUploaderByEntityId';
import { ViewPageBlock } from 'components/ViewPage/ViewPageBlock';
import { ViewPageSection } from 'components/ViewPage/ViewPageSection';
import { ItemList } from 'components/ViewPage//ItemList';
import { NoItemsNotification } from 'components/ViewPage/NoItemsNotification';
import { Badge } from 'components/Badge';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import { MenuTranslationForm } from 'components/TranslationForm/MenuTranslationForm';
import Button from 'components/Button/Button';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { ISelectOption } from 'components/Select/types';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import breadcrumbs from 'images/breadcrumbs.svg';
import style from './index.module.scss';
import { Portions } from './components/Portions';

export const ViewForm: React.FC = () => {
	const { featureFlags } = useContext<IAppContext>(AppContext);
	const {
		setTranslationTab,
		changeTypeOfModalWindow,
		setRestaurantIdForRestaurantProps,
		setRestaurantIdForPortions,
		changeSelectedPortionId,
		modifierFullInfo,
		accessibleRestaurants,
		accessiblePortionsRestaurants,
		selectedTranslationTab,
		restaurantIdForRestaurantProps,
		restaurantProps,
		restaurantIdForPortions,
		restaurantPortions,
	} = useContext<IViewModifierContext>(ViewModifierContext);
	const { t } = useTranslation('form');
	const hasPortions = restaurantProps?.hasPortions;
	const hasSchedulePriority = modifierFullInfo?.overrideScheduleAvailability;
	const schedulePriorityValue = hasSchedulePriority ? t('yes') : t('no');
	const listOfRestaurants = useMemo(() => accessibleRestaurants, [accessibleRestaurants]);
	const inStopValue = t('inStopLabel');
	const isRestrictedValue = t('isRestrictedLabel');
	const isPreselectedValue = t('isPreselectedLabel');
	const isForceDisabledValue = t('isForceDisabledLabel');
	const portionSelectionMode = portionSelectionModes.find(
		(mode) => mode.id === restaurantProps?.portionSelectionMode,
	);
	const portionSelectionModeValue = portionSelectionMode ? t(portionSelectionMode.name) : '-';
	const listOfChildModGroups = useMemo(
		() => restaurantProps?.childModifierGroups || [],
		[restaurantProps?.childModifierGroups],
	);

	const listOfParentModGroups = useMemo(
		() => restaurantProps?.parentModifierGroups || [],
		[restaurantProps?.parentModifierGroups],
	);
	const listOfPortionsRestaurants = useMemo(() => accessiblePortionsRestaurants, [accessiblePortionsRestaurants]);
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);

	const mappedRestaurantOptions = useMemo<ISelectOption[]>(() => {
		const visibleItems = showArchived
			? accessibleRestaurants
			: accessibleRestaurants.filter(
					(restaurant) =>
						restaurant.status === EntityStatus.Active || restaurant.id === restaurantIdForRestaurantProps,
			  );

		return visibleItems.map((restaurant) => ({
			...restaurant,
			additionalContent: restaurant.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [accessibleRestaurants, showArchived, restaurantIdForRestaurantProps]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	const handleClickAssignRestaurant = useCallback(
		() => changeTypeOfModalWindow('assignRestaurants'),
		[changeTypeOfModalWindow],
	);

	const handleClickEditNestedModGroupTemplate = useCallback(
		() => changeTypeOfModalWindow('editNestedModGroupTemplate'),
		[changeTypeOfModalWindow],
	);

	const handleClickViewPortion = useCallback(
		(value: string) => {
			changeSelectedPortionId(value);
			changeTypeOfModalWindow('viewPortion');
		},
		[changeTypeOfModalWindow],
	);

	const renderBadges = useMemo(() => {
		if (modifierFullInfo?.type || modifierFullInfo?.isCreatedOnPos) {
			return (
				<div className={style.badgesWrapper}>
					{modifierFullInfo.type && (
						<Badge color="red">
							<Text
								translationNameSpace="form"
								translationKey={modifierFullInfo.type}
								asFragment={true}
								data-test="modifierAsDish"
							/>
						</Badge>
					)}
					{modifierFullInfo.isCreatedOnPos ? (
						<Badge color="purple">
							<Text
								translationKey="POS"
								asFragment={true}
								data-test={`isCreatedOnPos=${modifierFullInfo.isCreatedOnPos}`}
							/>
						</Badge>
					) : (
						<Badge color="purple">
							<Text
								translationKey="AMS"
								asFragment={true}
								data-test={`isCreatedOnPos=${modifierFullInfo.isCreatedOnPos}`}
							/>
						</Badge>
					)}
				</div>
			);
		}
	}, [modifierFullInfo?.type, modifierFullInfo?.isCreatedOnPos]);

	return (
		<div className={style.container} data-test="ViewForm">
			<div className={style.content}>
				<ViewPageBlock heading="generalPropertiesHeading">
					<ViewPageSection heading="mainInfoHeading">
						<div className={style.row}>
							<div className={style.imgWrapper}>
								{modifierFullInfo?.id && modifierFullInfo.photoPath ? (
									<ImgUploaderByEntityId
										resource={RequestEntity.DishesMenuV2}
										entityID={modifierFullInfo.id}
										isMenuV2={true}
										infoContent={renderBadges}
										photoSize="Medium"
										loaderSize="big"
										key="1"
									/>
								) : (
									<ImgUploaderByEntityId
										resource={RequestEntity.DishesMenuV2}
										entityID=""
										isMenuV2={true}
										infoContent={renderBadges}
										photoSize="Medium"
										loaderSize="big"
										key="2"
									/>
								)}
							</div>
							<div className={style.mainInfoInputs}>
								{modifierFullInfo?.type === 'DishAndModifier' && (
									<Text
										translationNameSpace="form"
										translationKey="warningModifierAsDishChange"
										theme="secondary"
										size="s"
										display="inline"
									/>
								)}
								<FullInfoField label="nameModifierLabel" value={modifierFullInfo?.name || '-'} />
								<FullInfoField label="customerLabel" value={modifierFullInfo?.customer.name || '-'} />
								<FullInfoField
									label="articleNumberLabel"
									value={modifierFullInfo?.articleNumber || '-'}
								/>
							</div>
						</div>
					</ViewPageSection>

					<ViewPageSection heading="descriptionHeading">
						<FullInfoField label="descriptionLabel" value={modifierFullInfo?.description || '-'} />
					</ViewPageSection>

					<ViewPageSection heading="posNameHeading">
						<FullInfoField label="posNameLabel" value={modifierFullInfo?.posName || '-'} />
					</ViewPageSection>

					<ViewPageSection heading="idHeading">
						<FullInfoField label="externalIdLabel" value={modifierFullInfo?.externalId || '-'} />
					</ViewPageSection>

					<ViewPageSection heading="translationsHeading">
						<MenuTranslationForm
							className={style.translationForm}
							setTranslationTab={setTranslationTab}
							isShowLanguageName={false}
							IDSelectedCustomer={modifierFullInfo?.customer.id || ''}
							translations={modifierFullInfo?.translations || []}
							selectedTranslationTab={selectedTranslationTab}
							nameLabel="nameModifierLabel"
							hasDescriptionFullInfoField={true}
						/>
					</ViewPageSection>

					<ViewPageSection heading="scheduleHeading">
						<FullInfoField
							label="hasSchedulePriorityLabel"
							value={schedulePriorityValue}
							containerClassName={style.marginTopZero}
							prefixIcon="asCheckbox"
							prefixIconName={hasSchedulePriority ? 'checkMark' : 'crossMark'}
							prefixIconIsChecked={hasSchedulePriority}
						/>
						<div className={style.row}>
							<FullInfoField
								label="activationScheduleLabel"
								value={modifierFullInfo?.activationCron || '-'}
							/>
							<FullInfoField
								label="deactivationScheduleLabel"
								value={modifierFullInfo?.deactivationCron || '-'}
							/>
						</div>
					</ViewPageSection>

					<ViewPageSection heading="restaurantsHeading">
						{listOfRestaurants.length ? (
							<ItemList
								itemListLabel="allRestaurantsWithThisModifier"
								items={listOfRestaurants}
								pathToItem="/clients/restaurants?restaurantFullInfoID="
								assignBtnTranslationKey="editRestaurantsList"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						) : (
							<NoItemsNotification
								title="noRestaurantsAssigned"
								description="assignRestaurantsInTheCustomerModifierToBeAbleToEditThem"
								assignBtnTranslationKey="assignRestaurantsButton"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						)}
					</ViewPageSection>

					<div className={style.btnWrapper} data-test="BtnBlock1">
						{/* <Button theme="primaryBlue" disabled={true} onClick={() => console.log('Check tree view')}>
							<Text translationNameSpace="form" translationKey="checkTreeViewButton" asFragment={true} />
						</Button> */}
						<Link
							target={`blank_${Date.now()}`}
							rel="noopener noreferrer"
							to={`/menu-v2/modifiers/edit?modifierID=${modifierFullInfo?.id}`}>
							<Button
								className={style.btn}
								theme="primaryBlue"
								icon={
									<svg className={style.icon}>
										<use xlinkHref={`${breadcrumbs}#pencil`} />
									</svg>
								}>
								<Text translationNameSpace="form" translationKey="editButton" asFragment={true} />
							</Button>
						</Link>
					</div>
				</ViewPageBlock>

				<ViewPageBlock heading="restaurantPropertiesHeading">
					{accessibleRestaurants.length ? (
						<>
							<OutsideFormSelect
								testId="restaurantProperties"
								label="restaurantsLabel"
								name="restaurantProperties"
								value={restaurantIdForRestaurantProps}
								options={mappedRestaurantOptions}
								onChange={setRestaurantIdForRestaurantProps}
								keepSearchWithDropdownRender={true}
								dropdownRender={renderSelectMenu}
							/>
							<ViewPageSection heading="scheduleHeading">
								<div className={style.row}>
									<FullInfoField
										label="activationScheduleLabel"
										value={restaurantProps?.activationCron || '-'}
									/>
									<FullInfoField
										label="deactivationScheduleLabel"
										value={restaurantProps?.deactivationCron || '-'}
									/>
								</div>
							</ViewPageSection>

							<div className={style.row}>
								<ViewPageSection heading="inStopHeading">
									{restaurantProps ? (
										<FullInfoField
											label="inStopLabel"
											value={inStopValue}
											prefixIcon="asCheckbox"
											prefixIconName={restaurantProps?.isDisabled ? 'checkMark' : 'crossMark'}
											prefixIconIsChecked={restaurantProps?.isDisabled}
										/>
									) : (
										<FullInfoField label="inStopLabel" value="-" />
									)}
								</ViewPageSection>
								<ViewPageSection heading="isRestrictedHeading">
									{restaurantProps ? (
										<FullInfoField
											label="isRestrictedLabel"
											value={isRestrictedValue}
											prefixIcon="asCheckbox"
											prefixIconName={restaurantProps?.isRestricted ? 'checkMark' : 'crossMark'}
											prefixIconIsChecked={restaurantProps?.isRestricted}
										/>
									) : (
										<FullInfoField label="isRestrictedLabel" value="-" />
									)}
								</ViewPageSection>
							</div>

							<div className={style.row}>
								<ViewPageSection heading="isForceDisabledHeading">
									{restaurantProps ? (
										<FullInfoField
											label="isForceDisabledLabel"
											value={isForceDisabledValue}
											prefixIcon="asCheckbox"
											prefixIconName={restaurantProps?.forceDisable ? 'checkMark' : 'crossMark'}
											prefixIconIsChecked={restaurantProps?.forceDisable}
										/>
									) : (
										<FullInfoField label="isForceDisabledLabel" value="-" />
									)}
								</ViewPageSection>
								<ViewPageSection heading="isPreselectedHeading">
									{restaurantProps ? (
										<FullInfoField
											label="isPreselectedLabel"
											value={isPreselectedValue}
											prefixIcon="asCheckbox"
											prefixIconName={restaurantProps?.isPreselected ? 'checkMark' : 'crossMark'}
											prefixIconIsChecked={restaurantProps?.isPreselected}
										/>
									) : (
										<FullInfoField label="isPreselectedLabel" value="-" />
									)}
								</ViewPageSection>
							</div>

							<ViewPageSection heading="pricingStrategyHeading">
								<FullInfoField label="portionSelectionModeLabel" value={portionSelectionModeValue} />
							</ViewPageSection>

							<ViewPageSection heading="priceHeading">
								<div className={hasPortions ? style.column : style.row}>
									{hasPortions ? (
										<Text
											translationNameSpace="form"
											translationKey="warningPriceSupplementPriceHiddenByModifierPortions"
											theme="secondary"
											size="s"
											display="inline"
										/>
									) : (
										<>
											<FullInfoField
												label="signedPriceLabel"
												value={
													typeof restaurantProps?.price === 'number'
														? `${restaurantProps.currencySymbol || ''} ${
																restaurantProps.price || 0
														  }`
														: '-'
												}
											/>
											<FullInfoField
												label="signedSupplementPriceLabel"
												value={
													typeof restaurantProps?.supplementPrice === 'number'
														? `${restaurantProps.currencySymbol || ''} ${
																restaurantProps.supplementPrice || 0
														  }`
														: '-'
												}
											/>
										</>
									)}
								</div>
							</ViewPageSection>
							<ViewPageSection heading="maxLimitOfQuantityHeading">
								{restaurantProps ? (
									<FullInfoField
										label="maxLimitOfQuantityLabel"
										value={restaurantProps?.quantityMaxLimit}
										containerClassName={style.marginTopZero}
									/>
								) : (
									<FullInfoField label="maxLimitOfQuantityLabel" value="-" />
								)}
							</ViewPageSection>
							<ViewPageSection heading="modifierParentModifierGroupsHeading">
								{restaurantProps && !!listOfParentModGroups.length ? (
									<div className={style.elementsList}>
										{listOfParentModGroups.map((modifierGroup) => {
											return (
												<div key={modifierGroup.id} className={style.elementItem}>
													<div className={style.dot} />
													<Link
														className={style.link}
														target={`blank_${Date.now()}`}
														rel="noopener noreferrer"
														to={`/menu-v2/modifier-groups/view?modifierGroupID=${modifierGroup.id}`}>
														<Text
															className={style.name}
															translationKey={modifierGroup.name}
															size="s"
															withTooltip={true}
														/>
													</Link>
													{modifierGroup.isDefault && (
														<Badge color="green" size="sm">
															<Text
																translationNameSpace="form"
																translationKey="isDefaultLabel"
																size="xs"
															/>
														</Badge>
													)}
													{modifierGroup.isAssignedToPortion && (
														<Badge color="blue" size="sm">
															<Text
																translationNameSpace="form"
																translationKey="posPortionLabel"
																size="xs"
															/>
														</Badge>
													)}
												</div>
											);
										})}
									</div>
								) : (
									<Text translationNameSpace="form" translationKey="-" />
								)}
							</ViewPageSection>

							<ViewPageSection heading="modifierChildModifierGroupsHeading">
								{restaurantProps ? (
									<>
										{!restaurantProps?.hasPortionBasedNestedModGroups &&
											!!listOfChildModGroups.length && (
												<ItemList
													items={listOfChildModGroups}
													pathToItem="/menu-v2/modifier-groups/view?modifierGroupID="
													assignBtnTranslationKey="editNestedModGroupTemplateButton"
													onClickShowAssignModal={handleClickEditNestedModGroupTemplate}
												/>
											)}
										{!restaurantProps?.hasPortionBasedNestedModGroups &&
											!listOfChildModGroups.length && (
												<NoItemsNotification
													title="noNestedModifierGroupsAssigned"
													description="assignNestedModifierGroupsToModifierToBeAbleToEditTemplates"
													assignBtnTranslationKey="editNestedModGroupTemplateButton"
													onClickShowAssignModal={handleClickEditNestedModGroupTemplate}
													iconId="pencil"
												/>
											)}
										{!!restaurantProps?.hasPortionBasedNestedModGroups && (
											<Text
												translationNameSpace="form"
												translationKey="warningNestedModGroupsHiddenByModifierPortions"
												theme="secondary"
												size="s"
												display="inline"
											/>
										)}
									</>
								) : (
									<Text translationNameSpace="form" translationKey="-" />
								)}
							</ViewPageSection>
						</>
					) : (
						<ViewPageSection heading="restaurantsHeading">
							<NoItemsNotification
								title="noRestaurantsAssigned"
								description="assignRestaurantsInTheCustomerSubcategoryToBeAbleToEditThem"
								assignBtnTranslationKey="assignRestaurantsButton"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						</ViewPageSection>
					)}

					{restaurantIdForRestaurantProps && (
						<div className={style.btnWrapper} data-test="BtnBlock2">
							<Link
								target={`blank_${Date.now()}`}
								rel="noopener noreferrer"
								to={`/menu-v2/modifiers/edit-restaurant-properties?modifierID=${
									modifierFullInfo?.id || ''
								}`}>
								<Button
									className={style.btn}
									theme="primaryBlue"
									icon={
										<svg className={style.icon}>
											<use xlinkHref={`${breadcrumbs}#pencil`} />
										</svg>
									}>
									<Text translationNameSpace="form" translationKey="editButton" asFragment={true} />
								</Button>
							</Link>
						</div>
					)}
				</ViewPageBlock>
				{featureFlags.HAS_PORTION_IN_MENU_V2 && (
					<ViewPageBlock heading="portionsHeading">
						{listOfPortionsRestaurants.length ? (
							<>
								<OutsideFormSelect
									testId="restaurantPortions"
									label="restaurantsLabel"
									name="portionsRestaurantProperties"
									value={restaurantIdForPortions}
									options={accessiblePortionsRestaurants}
									onChange={setRestaurantIdForPortions}
								/>
								<Portions
									data={restaurantPortions ?? []}
									onClickInfo={handleClickViewPortion}
									//TODO: add edit and delete after MVP
									onClickEdit={handleClickViewPortion}
									onClickDelete={handleClickViewPortion}
								/>
							</>
						) : (
							<>
								<Text size="s" translationNameSpace="form" translationKey="noPortionsCreated" />
								<FullInfoField containerClassName={style.marginTopZero} label="noModifierPortions" />
							</>
						)}
					</ViewPageBlock>
				)}
			</div>
		</div>
	);
};
